import { AuthorManager } from '../AuthorManager';
import { IAuthor } from '../../types/Author';

/**
 * Shared utilities for attaching author information to entities
 */

/**
 * Attach author to an entity that has an author reference
 */
export async function attachAuthorToEntity<T extends { author?: IAuthor | null; ownership?: { recordingAuthor?: string | null } }>(
  entity: T
): Promise<T & { author: IAuthor | null }> {
  if (entity.ownership?.recordingAuthor) {
    const author = await AuthorManager.getAuthor(entity.ownership.recordingAuthor);
    return { ...entity, author } as T & { author: IAuthor | null };
  }
  return { ...entity, author: null } as T & { author: IAuthor | null };
}

/**
 * Attach authors to multiple entities
 * Efficiently batches author fetches to avoid duplicate requests
 */
export async function attachAuthorsToEntities<T extends { author?: IAuthor | null; ownership?: { recordingAuthor?: string | null } }>(
  entities: T[]
): Promise<Array<T & { author: IAuthor | null }>> {
  // Collect unique author keys
  const authorKeys = new Set<string>();
  entities.forEach(entity => {
    if (entity.ownership?.recordingAuthor) {
      authorKeys.add(entity.ownership.recordingAuthor);
    }
  });

  // Fetch all authors in parallel
  const authorPromises = Array.from(authorKeys).map(key => 
    AuthorManager.getAuthor(key).then(author => ({ key, author }))
  );
  const authorResults = await Promise.all(authorPromises);

  // Create a map for quick lookup
  const authorMap = new Map<string, IAuthor | null>();
  authorResults.forEach(({ key, author }) => {
    authorMap.set(key, author);
  });

  // Attach authors to entities
  return entities.map(entity => {
    if (entity.ownership?.recordingAuthor) {
      const author = authorMap.get(entity.ownership.recordingAuthor) || null;
      return { ...entity, author } as T & { author: IAuthor | null };
    }
    return { ...entity, author: null } as T & { author: IAuthor | null };
  });
}

/**
 * Extract unique author keys from entities
 */
export function extractAuthorKeys<T extends { ownership?: { recordingAuthor?: string | null } }>(
  entities: T[]
): string[] {
  const authorKeys = new Set<string>();
  entities.forEach(entity => {
    if (entity.ownership?.recordingAuthor) {
      authorKeys.add(entity.ownership.recordingAuthor);
    }
  });
  return Array.from(authorKeys);
}
